/**
 * Waiting for a conversion job: polls GET /api/v1/jobs/{id} with a growing
 * delay up to a cap, tolerates a few transient failures, and gives up after
 * a total deadline (the job keeps running on the server; its result appears
 * in the Inbox).
 */

import { getJob } from "../api/client";
import type { Job } from "../api/types";
import { ApiError, InvalidResponseError, NetworkError } from "../lib/http";

export const FIRST_DELAY_MS = 1_000;
export const MAX_DELAY_MS = 5_000;
const BACKOFF_FACTOR = 1.5;
const MAX_WAIT_MS = 60 * 60 * 1_000;
export const MAX_CONSECUTIVE_FAILURES = 5;

export class JobTimeoutError extends Error {
  override name = "JobTimeoutError";
}

interface WaitOptions {
  readonly signal: AbortSignal;
  readonly onUpdate?: (job: Job) => void;
  readonly fetchJob?: (id: string, signal: AbortSignal) => Promise<Job>;
  readonly sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  readonly now?: () => number;
}

/** Resolve after `ms`, or reject with an AbortError when `signal` aborts. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Cancelled", "AbortError"));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException("Cancelled", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function isTransient(error: unknown): boolean {
  if (error instanceof NetworkError || error instanceof InvalidResponseError)
    return true;
  return (
    error instanceof ApiError && (error.status === 429 || error.status >= 500)
  );
}

function isFinished(job: Job): boolean {
  return job.status === "succeeded" || job.status === "failed";
}

/** Poll until the job succeeds or fails; returns the finished job. */
export async function waitForJob(
  id: string,
  options: WaitOptions,
): Promise<Job> {
  const fetchJob = options.fetchJob ?? getJob;
  const pause = options.sleep ?? sleep;
  const now = options.now ?? Date.now;
  const deadline = now() + MAX_WAIT_MS;
  let delay = FIRST_DELAY_MS;
  let failures = 0;
  for (;;) {
    await pause(delay, options.signal);
    try {
      const job = await fetchJob(id, options.signal);
      failures = 0;
      options.onUpdate?.(job);
      if (isFinished(job)) return job;
    } catch (error) {
      failures += 1;
      if (!isTransient(error) || failures >= MAX_CONSECUTIVE_FAILURES)
        throw error;
    }
    if (now() >= deadline) {
      throw new JobTimeoutError(
        "The conversion is taking longer than expected. The result will appear in your Inbox when it is ready.",
      );
    }
    delay = Math.min(MAX_DELAY_MS, delay * BACKOFF_FACTOR);
  }
}
